import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';

import { useApi } from '@/lib/api-context';

// Control de cambios del documento (spec 2026-09-11 §3.1): qué cambió de la versión
// anterior a la nueva. Se lista por documento y se guarda el de la versión en curso;
// guardar refresca las capacidades del flujo (desbloquea el envío a aprobación).

const DOCS = '/api/v1/quality/documents';

export const changeLogSchema = z.object({
  id: z.string(),
  version: z.number(),
  summary: z.string(),
  isCriticalChange: z.boolean(),
  updatedAt: z.string(),
});
export type ChangeLog = z.infer<typeof changeLogSchema>;

const listResponseSchema = z.object({ success: z.literal(true), data: z.array(changeLogSchema) });
const itemResponseSchema = z.object({ success: z.literal(true), data: changeLogSchema });

export function useChangeLogs(
  entryId: string,
  { companyId, enabled }: { companyId: string | null; enabled: boolean },
): UseQueryResult<ChangeLog[]> {
  const api = useApi();
  return useQuery({
    queryKey: ['change-log', companyId, entryId],
    queryFn: async () => {
      const raw = await api.get<unknown>(`${DOCS}/${entryId}/change-log`);
      return listResponseSchema.parse(raw).data;
    },
    enabled,
  });
}

export function useSaveChangeLog(
  entryId: string,
): UseMutationResult<ChangeLog, Error, { summary: string; isCriticalChange: boolean }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const raw = await api.post<unknown>(`${DOCS}/${entryId}/change-log`, body);
      return itemResponseSchema.parse(raw).data;
    },
    // También al fallar: un 409 (congelado o sin flujo) significa que la pantalla quedó
    // desactualizada respecto del flujo real.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['change-log'] });
      void qc.invalidateQueries({ queryKey: ['flow-capabilities'] });
    },
  });
}
